import { useEffect } from 'react';
import { useAuthStore } from '../store/authStore';

const WS_URL = 'wss://online.legitfam.pro/ws';
const RECONNECT_DELAY = 3000; // 3 секунды
const PING_INTERVAL = 30000; // 30 секунд

/**
 * Глобальный менеджер WebSocket соединения
 * Позволяет переиспользовать одно соединение между всеми компонентами
 */
class OnlineWebSocketManager {
  private ws: WebSocket | null = null;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private pingInterval: NodeJS.Timeout | null = null;
  private token: string | null = null;
  private subscribers: Set<() => void> = new Set();

  /**
   * Подписаться на изменения состояния соединения
   */
  subscribe(callback: () => void) {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  }

  /**
   * Уведомить всех подписчиков об изменении состояния
   */
  private notify() {
    this.subscribers.forEach(callback => callback());
  }

  /**
   * Получить текущее состояние соединения
   */
  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Подключиться к WebSocket серверу
   */
  connect(authToken: string) {
    // Если уже подключены с тем же токеном, ничего не делаем
    if (this.token === authToken && (this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING)) {
      return;
    }

    // Если токен изменился, переподключаемся
    if (this.token !== authToken) {
      this.disconnect();
    }

    this.token = authToken;

    try {
      const ws = new WebSocket(WS_URL);

      ws.onopen = () => {
        // Запускаем пинги для поддержания соединения
        this.pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, PING_INTERVAL);

        this.notify();
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Обработка pong ответа
          if (data.type === 'pong') {
            // Соединение живо
            return;
          }
          
          // Здесь можно добавить обработку других типов сообщений
        } catch (error) {
          // Игнорируем ошибки парсинга
        }
      };

      ws.onerror = () => {
        // Ошибки соединения обрабатываются в onclose
      };

      ws.onclose = () => {
        // Очищаем интервал пингов
        if (this.pingInterval) {
          clearInterval(this.pingInterval);
          this.pingInterval = null;
        }

        this.notify();

        // Пытаемся переподключиться через некоторое время, если токен есть
        if (this.token) {
          this.reconnectTimeout = setTimeout(() => {
            if (this.token) {
              this.connect(this.token);
            }
          }, RECONNECT_DELAY);
        }
      };

      this.ws = ws;
    } catch (error) {
      // Попытка переподключения
      if (this.token) {
        this.reconnectTimeout = setTimeout(() => {
          if (this.token) {
            this.connect(this.token);
          }
        }, RECONNECT_DELAY);
      }
    }
  }

  /**
   * Отключиться от WebSocket сервера
   */
  disconnect() {
    // Очистка таймеров
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }

    // Закрытие WebSocket соединения
    if (this.ws) {
      this.ws.onclose = null; // Убираем обработчик, чтобы не было автоматического переподключения
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.close();
      }
      this.ws = null;
    }

    this.token = null;
    this.notify();
  }
}

// Глобальный экземпляр менеджера
const wsManager = new OnlineWebSocketManager();

/**
 * Хук для подключения к WebSocket серверу отслеживания онлайна
 * Автоматически переподключается при разрыве соединения
 * Использует глобальное соединение, которое не пересоздается при смене страниц
 */
export const useOnlineWebSocket = () => {
  const { isAuthenticated, token } = useAuthStore();

  useEffect(() => {
    if (isAuthenticated && token) {
      // Подключаемся к глобальному соединению
      wsManager.connect(token);
    } else {
      // Если пользователь разлогинился, закрываем соединение
      wsManager.disconnect();
    }

    // При размонтировании компонента НЕ закрываем соединение,
    // так как оно может использоваться другими компонентами
  }, [isAuthenticated, token]);

  return {
    isConnected: wsManager.isConnected,
  };
};
